import { Box } from '@mui/material';
import useModal from 'common/hooks/useModal';
import { m } from 'framer-motion';
import { memo, useEffect, useRef, useState } from 'react';
import LoadingScreen from './LoadingScreen';
import BasicModal from './modal/BasicModal';
const modalStyles = {
  width: 'fit-content',
  minWidth: '80%',
  maxHeight: '95%',
  overflowY: 'hidden',
  overflowX: 'hidden',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  borderRadius: '15px',
  margin: 0,
  padding: 0,
} as const;

const HtmlRenderModal = () => {
  const { skillUrl, setSkillUrl } = useModal();
  const [isLoaded, setIsLoaded] = useState(false);
  const handleClose = () => {
    setSkillUrl('');
    setIsLoaded(false);
  };

  return (
    <BasicModal sx={modalStyles} open={Boolean(skillUrl)} onClose={handleClose}>
      <Box
        sx={{
          height: '90vh',
          width: 'calc(100% - 10px)',
          padding: '10px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <ModalIframe src={skillUrl} isLoaded={isLoaded} setIsLoaded={setIsLoaded} />
        <Box
          sx={{
            width: '100%',
            height: '100%',
            alignItems: 'center',
            justifyContent: 'center',
            display: isLoaded ? 'none' : 'flex',
          }}
        >
          <LoadingScreen positionFixed={false} />
        </Box>
      </Box>
    </BasicModal>
  );
};

export default memo(HtmlRenderModal);

interface ModalIframeProps {
  src: string;
  setIsLoaded: (isLoaded: boolean) => void;
  isLoaded: boolean;
}

const ModalIframe = ({ src, setIsLoaded, isLoaded }: ModalIframeProps) => {
  return (
    <m.iframe
      src={src}
      key={src} // Add key prop to force remount
      initial={{
        scale: 0,
        opacity: 0,
      }}
      exit={{
        scale: 0,
        opacity: 0,
      }}
      transition={{
        duration: 1,
      }}
      animate={{
        scale: isLoaded ? 1 : 0,
        opacity: isLoaded ? 1 : 0,
      }}
      style={{
        border: 'none',
        width: isLoaded ? '100%' : 0,
        height: isLoaded ? '100%' : 0,
        visibility: isLoaded ? 'visible' : 'hidden',
      }}
      onLoad={() => {
        setIsLoaded(true);
      }}
    />
  );
};

// For rendering html string if needed
export const ModalHtmlContent = ({ htmlContent }: { htmlContent: string }) => {
  const contentRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!contentRef.current) return;
    const shadowRoot = contentRef.current.attachShadow({ mode: 'closed' });
    const container = document.createElement('div');
    container.innerHTML = htmlContent;
    shadowRoot.appendChild(container);
    container.scrollTop = 0;
    // Cleanup function
    return () => {
      if (contentRef.current?.shadowRoot) {
        contentRef.current.shadowRoot.innerHTML = '';
      }
    };
  }, [htmlContent]);

  return (
    <m.div
      initial={{
        scale: 0,
        opacity: 0,
        // rotate: 0,
      }}
      transition={{
        duration: 1,
      }}
      animate={{
        scale: 1,
        opacity: 1,
        // rotate: 360,
      }}
      style={{
        overflowY: 'auto', // Ensures scrolling within the modal
        maxHeight: '100%', // Or set to the max height you need
        width: 'fit-content', // Full width within the modal container
        margin: '20px 0px',
        padding: '30px',
      }}
      ref={contentRef}
    />
  );
};
